import { portalApi } from "../lib/portalApiClient";
import { getApiErrorStatus } from "../utils/apiError";

export interface PortalPasswordSession {
  sessionToken: string;
  email: string;
  practiceNames: string[];
}

export interface PortalPasswordStatus {
  hasPassword: boolean;
}

export interface PortalPasswordManagementError extends Error {
  refreshPasswordStatus?: boolean;
}

function managementError(
  message: string,
  refreshPasswordStatus = false
): PortalPasswordManagementError {
  const error: PortalPasswordManagementError = new Error(message);
  error.refreshPasswordStatus = refreshPasswordStatus;
  return error;
}

function accountPasswordError(error: unknown): Error {
  const status = getApiErrorStatus(error);
  if (status === 428) {
    return managementError(
      "This account already has a password. Enter the current password to change it.",
      true
    );
  }
  if (status === 422) {
    return managementError("The current password is incorrect.");
  }
  if (status === 409) {
    return new Error("Your new password must differ from your current one.");
  }
  if (status === 400) {
    return new Error("Check the password requirements and try again.");
  }
  if (status === 429) {
    return new Error("Too many attempts. Please wait before trying again.");
  }
  return new Error("We couldn't update your password. Please try again.");
}

function resetPasswordError(error: unknown): Error {
  const status = getApiErrorStatus(error);
  if (status === 401) {
    return new Error(
      "That verification code is incorrect or has expired. Request a new code and try again."
    );
  }
  if (status === 400) {
    return new Error("Check the password requirements and try again.");
  }
  if (status === 429) {
    return new Error("Too many attempts. Please wait before trying again.");
  }
  return new Error("We couldn't reset your password. Please try again.");
}

export const portalPasswordService = {
  login(email: string, password: string): Promise<PortalPasswordSession> {
    return portalApi.post("/auth/password/login", { email, password });
  },

  register(
    invitationToken: string,
    password: string
  ): Promise<PortalPasswordSession> {
    return portalApi.post("/auth/register/password", {
      invitationToken,
      password,
    });
  },

  requestReset(email: string): Promise<{ sent: boolean }> {
    return portalApi.post("/auth/password/reset/request", { email });
  },

  async reset(
    email: string,
    code: string,
    newPassword: string
  ): Promise<{ reset: boolean }> {
    try {
      return await portalApi.post("/auth/password/reset", {
        email,
        code,
        newPassword,
      });
    } catch (error) {
      throw resetPasswordError(error);
    }
  },

  async getStatus(): Promise<PortalPasswordStatus> {
    try {
      return await portalApi.get("/account/password");
    } catch {
      throw new Error("We couldn't load your password settings.");
    }
  },

  async manage(
    newPassword: string,
    currentPassword?: string
  ): Promise<{ updated: boolean }> {
    try {
      return await portalApi.put("/account/password", {
        newPassword,
        currentPassword,
      });
    } catch (error) {
      throw accountPasswordError(error);
    }
  },
};
